import { useState, useCallback, useEffect, useRef } from 'react';
import { useScore } from '../../../context/ScoreContext';
import {
  scheduleDays,
  STATUS_CONFIG,
  buildEventKey,
  buildTaskKey,
  MISSION_TASK_KEY,
  HIDDEN_MISSION_EVENT,
  HIDDEN_MISSION_INSERT_AFTER_DAY,
  HIDDEN_MISSION_INSERT_AFTER_IDX,
  TaskStatus,
} from '../../../data/scheduleData';
import SecretAngelRevealCeremony from './SecretAngelRevealCeremony';
import { useHiddenMissionTimer } from '../../../hooks/useHiddenMissionTimer';
import { useSoundEffect } from '../../../hooks/useSoundEffect';

const CLAIMED_KEY = 'srs-claimed-rewards-v1';
const REWARD_HISTORY_KEY = 'srs-reward-history-v2';

export interface RewardHistoryEntry {
  eventKey: string;
  eventTitle: string;
  dayLabel: string;
  points: number;
  timestamp: number;
  team: 'black' | 'white';
}

export function getMemberRewardHistory(memberId: string): RewardHistoryEntry[] {
  try {
    const all = JSON.parse(localStorage.getItem(REWARD_HISTORY_KEY) ?? '{}') as Record<string, RewardHistoryEntry[]>;
    return all[memberId] ?? [];
  } catch { return []; }
}

function saveMemberRewardEntry(memberId: string, entry: RewardHistoryEntry) {
  try {
    const all = JSON.parse(localStorage.getItem(REWARD_HISTORY_KEY) ?? '{}') as Record<string, RewardHistoryEntry[]>;
    const prev = all[memberId] ?? [];
    all[memberId] = [entry, ...prev];
    localStorage.setItem(REWARD_HISTORY_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

function getClaimedMap(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(CLAIMED_KEY) ?? '{}'); }
  catch { return {}; }
}
function markClaimed(key: string) {
  const map = getClaimedMap(); map[key] = true;
  localStorage.setItem(CLAIMED_KEY, JSON.stringify(map));
}

// ── Blue status theme ────────────────────────────────────────────────────────
const STATUS_BLUE: Record<TaskStatus, { color: string; bg: string; border: string }> = {
  pending: { color: '#3a5a78', bg: '#0a1830',   border: '#0d2040' },
  ongoing: { color: '#4da8ff', bg: '#4da8ff18', border: '#4da8ff35' },
  done:    { color: '#7ec8a0', bg: '#7ec8a018', border: '#7ec8a030' },
};

interface RewardPopup { points: number; team: 'black' | 'white'; eventTitle: string; }
interface UnlockToast { id: string; taskName: string; }

// ── Particle ─────────────────────────────────────────────────────────────────
interface Particle { id: number; x: number; delay: number; size: number; color: string; duration: number; xDrift: number; }

// ── Day complete overlay ──────────────────────────────────────────────────────
function DayCompleteEffect({ onDone }: { onDone: () => void }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const t0 = setTimeout(() => setVisible(true), 40);
    const t1 = setTimeout(() => {
      setExiting(true);
      setTimeout(onDone, 480);
    }, 3800);
    return () => { clearTimeout(t0); clearTimeout(t1); };
  }, [onDone]);

  const handleClick = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(onDone, 480);
  };

  const COLORS = ['#4da8ff', '#60d4ff', '#a0e8ff', '#7ec8a0', '#9090ff', '#60ffcc'];
  const particles: Particle[] = Array.from({ length: 50 }, (_, i) => ({
    id: i, x: 3 + Math.random() * 94, delay: Math.random() * 1.4,
    size: Math.random() * 7 + 3, color: COLORS[i % COLORS.length],
    duration: 1.3 + Math.random() * 1.6, xDrift: (Math.random() - 0.5) * 140,
  }));

  return (
    <div
      className="fixed inset-0 z-[60] overflow-hidden cursor-pointer select-none"
      onClick={handleClick}
      style={{
        opacity: exiting ? 0 : visible ? 1 : 0,
        transition: exiting
          ? 'opacity 0.45s ease-out'
          : 'opacity 0.35s ease-in',
        willChange: 'opacity',
      }}
    >
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
      {particles.map(p => (
        <div key={p.id} className="absolute rounded-full" style={{
          left: `${p.x}%`, bottom: '0', width: p.size, height: p.size,
          background: p.color, boxShadow: `0 0 ${p.size * 2.5}px ${p.color}`,
          '--x-drift': `${p.xDrift}px`,
          animation: `dcParticle ${p.duration}s ${p.delay}s cubic-bezier(0.2,0.8,0.4,1) forwards`,
          pointerEvents: 'none',
        } as React.CSSProperties} />
      ))}
      {[0, 0.3, 0.6].map((delay, i) => (
        <div key={i} className="absolute rounded-full" style={{
          left: '50%', top: '45%', transform: 'translate(-50%, -50%)',
          width: 120 + i * 60, height: 120 + i * 60,
          border: '1.5px solid #4da8ff',
          animation: `dcRing 1.6s ${delay}s ease-out forwards`,
          pointerEvents: 'none',
        }} />
      ))}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div className="text-center px-10 py-8 rounded-2xl" style={{
          background: 'linear-gradient(135deg, #04102299, #020b1aaa)',
          border: '1.5px solid #4da8ff55', boxShadow: '0 0 80px rgba(77,168,255,0.35)',
          animation: 'dcPop 0.55s cubic-bezier(0.175,0.885,0.32,1.275)',
        }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{
            background: 'radial-gradient(circle, #4da8ff35, #4da8ff0a)',
            border: '2px solid #4da8ff55', boxShadow: '0 0 35px rgba(77,168,255,0.55)',
          }}>
            <i className="ri-trophy-fill text-4xl" style={{ color: '#4da8ff' }} />
          </div>
          <p className="text-white text-2xl font-black tracking-widest mb-2" style={{ textShadow: '0 0 24px #4da8ff, 0 0 50px rgba(77,168,255,0.5)' }}>
            任務全數完成！
          </p>
          <p style={{ color: '#60c8ff', fontSize: '0.78rem', letterSpacing: '0.3em' }}>今日冒險圓滿落幕</p>
        </div>
        {/* Tap hint */}
        <p
          className="tracking-widest uppercase"
          style={{ fontSize: '10px', color: 'rgba(77,168,255,0.4)', fontFamily: "'Cinzel', serif", animation: 'dcFadeHint 0.5s ease-out 1s both' }}
        >
          點擊畫面繼續
        </p>
      </div>
      <style>{`
        @keyframes dcParticle { 0%{transform:translateY(0) translateX(0) scale(1);opacity:1} 85%{opacity:0.6} 100%{transform:translateY(-90vh) translateX(var(--x-drift,0px)) scale(0.25);opacity:0} }
        @keyframes dcRing { 0%{opacity:0.9;transform:translate(-50%,-50%) scale(0.3)} 100%{opacity:0;transform:translate(-50%,-50%) scale(1.6)} }
        @keyframes dcPop { from{opacity:0;transform:scale(0.6)} to{opacity:1;transform:scale(1)} }
        @keyframes dcFadeHint { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ── Unlock Toast ──────────────────────────────────────────────────────────────
function UnlockToastStack({ toasts }: { toasts: UnlockToast[] }) {
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[55] flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map(t => (
        <div
          key={t.id}
          className="flex items-center gap-3 px-5 py-3 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, #04122a, #020d1e)',
            border: '1.5px solid #4da8ff55',
            boxShadow: '0 0 30px rgba(77,168,255,0.4), 0 0 60px rgba(77,168,255,0.15)',
            animation: 'unlockToast 4s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
          }}
        >
          {/* animated unlock icon */}
          <div className="relative w-8 h-8 flex items-center justify-center rounded-full shrink-0"
            style={{ background: '#4da8ff20', border: '1.5px solid #4da8ff50', boxShadow: '0 0 12px rgba(77,168,255,0.5)' }}>
            <i className="ri-lock-unlock-fill text-sm" style={{ color: '#4da8ff', animation: 'unlockIconPop 0.6s cubic-bezier(0.175,0.885,0.32,1.275)' }} />
            {/* ring pulse */}
            <div className="absolute inset-0 rounded-full" style={{ border: '1px solid #4da8ff', animation: 'unlockRingPulse 1s ease-out 0.2s infinite' }} />
          </div>

          <div>
            <p className="text-[9px] font-black tracking-[0.35em] uppercase" style={{ color: '#4da8ff', textShadow: '0 0 8px #4da8ff80' }}>
              任務已解鎖
            </p>
            <p className="text-white text-xs font-bold mt-0.5 max-w-[180px] truncate">{t.taskName}</p>
          </div>

          {/* right shimmer beam */}
          <div className="w-px h-6 mx-1 shrink-0" style={{ background: 'linear-gradient(180deg, transparent, #4da8ff60, transparent)' }} />
          <i className="ri-sparkling-2-fill text-sm shrink-0" style={{ color: '#4da8ff', animation: 'sparkle 1s ease-in-out 0.3s infinite alternate' }} />
        </div>
      ))}
    </div>
  );
}

const BADGE_COLORS: Record<string, string> = {
  '任務': '#d4a843', '互動': '#7b68ee', '競賽': '#e05555',
  '獎勵': '#7ec8a0', '儀式': '#c06b9a', '機制': '#f0a86e',
};

interface CustomTaskRowProps {
  task: import('../../../data/scheduleData').CustomTask;
  isClaimed: boolean;
  isClaiming: boolean;
  selectedTeam: 'black' | 'white' | null;
  currentMember: { id: string; name: string } | null;
  onClaim: (claimKey: string, title: string) => void;
}

function CustomTaskRow({ task, isClaimed, isClaiming, selectedTeam, currentMember, onClaim }: CustomTaskRowProps) {
  const [expanded, setExpanded] = useState(false);
  const badgeColor = BADGE_COLORS[task.badge] ?? '#e05555';
  const taskStatus = task.status ?? 'ongoing';
  const isOngoing = taskStatus === 'ongoing';
  const isDone = taskStatus === 'done';
  const claimKey = `custom-${task.id}`;
  const teamColor = selectedTeam === 'black' ? '#d4a843' : '#c0c0c0';

  return (
    <div className="rounded-lg overflow-hidden border transition-all duration-300"
      style={{
        background: isDone ? 'rgba(126,200,160,0.04)' : 'transparent',
        borderColor: isDone ? '#7ec8a015' : 'transparent',
      }}>
      {/* Task row */}
      <button
        onClick={() => task.desc ? setExpanded(v => !v) : undefined}
        className={`w-full flex items-center gap-2 py-2 px-2 rounded-md text-left group relative overflow-hidden ${task.desc ? 'cursor-pointer hover:bg-[#e0555508]' : 'cursor-default'}`}
      >
        <div className="w-4 h-4 flex items-center justify-center shrink-0">
          {isDone
            ? <i className="ri-check-line text-[10px]" style={{ color: '#7ec8a0' }} />
            : <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#e05555', boxShadow: '0 0 5px #e05555' }} />
          }
        </div>
        <span className={`text-sm font-medium flex-1 ${isDone ? 'line-through' : 'text-[#e8a0a0]'}`}
          style={{ color: isDone ? '#3a5a4a' : undefined }}>
          {task.title}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {isOngoing && (
            <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-0.5"
              style={{ background: '#e0555512', color: '#e05555', border: '1px solid #e0555525' }}>
              <i className="ri-loader-4-line animate-spin text-[8px]" />進行中
            </span>
          )}
          {isDone && (
            <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-0.5"
              style={{ background: '#7ec8a010', color: '#7ec8a0', border: '1px solid #7ec8a020' }}>
              <i className="ri-checkbox-circle-fill text-[8px]" />已完成
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded font-bold"
            style={{ background: `${badgeColor}20`, color: badgeColor }}>
            {task.badge}
          </span>
          {task.desc && (
            <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 text-[#4a2a2a] ${expanded ? 'rotate-180' : ''}`} />
          )}
        </div>
      </button>

      {/* Expanded desc */}
      {task.desc && expanded && (
        <div className="mx-2 mb-2 px-3 py-3 rounded-md border-l-2" style={{ background: '#e0555508', borderColor: '#e0555540' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap"
              style={{ background: `${badgeColor}20`, color: badgeColor }}>
              {task.badge}
            </span>
            <span className="text-white text-xs font-bold">{task.title}</span>
          </div>
          <p className="text-[#6a3a3a] text-xs leading-relaxed whitespace-pre-line">{task.desc}</p>
        </div>
      )}

      {/* Reward button when done */}
      {isDone && (
        <div className="px-2 pb-2 border-t border-[#7ec8a010] pt-2">
          {!selectedTeam || !currentMember ? (
            <div className="flex items-center gap-2 text-[#1a3a58] text-[10px]">
              <i className="ri-gift-line text-sm" /><span>加入隊伍後可領取獎勵</span>
            </div>
          ) : isClaimed ? (
            <div className="flex items-center gap-1.5 text-[#7ec8a0] text-[10px]">
              <i className="ri-checkbox-circle-fill text-sm" /><span className="font-bold">獎勵已領取</span>
            </div>
          ) : (
            <button
              onClick={() => onClaim(claimKey, task.title)}
              disabled={isClaiming}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all overflow-hidden whitespace-nowrap"
              style={{
                background: selectedTeam === 'black' ? 'linear-gradient(135deg, #d4a84318, #d4a84308)' : 'linear-gradient(135deg, #c0c0c018, #c0c0c008)',
                border: `1px solid ${teamColor}40`,
                color: teamColor,
              }}>
              {isClaiming
                ? <><i className="ri-loader-4-line text-xs animate-spin" /><span>開獎中...</span></>
                : <>
                    <i className="ri-gift-2-line text-xs" />
                    <span>領取任務完成獎勵</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black ml-0.5"
                      style={{ background: `${teamColor}25`, color: teamColor }}>隨機 1–10 默契分</span>
                  </>
              }
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ScheduleSection() {
  const { scheduleState, selectedTeam, addScore, currentMember, scores } = useScore();
  const { eventStatuses, unlockedTasks, secretPairings, customTasks } = scheduleState;
  const taskOverrides  = scheduleState.taskOverrides  ?? {};
  const eventOverrides = scheduleState.eventOverrides ?? {};
  const taskAdditions  = scheduleState.taskAdditions  ?? {};
  const taskDeletions  = scheduleState.taskDeletions  ?? {};
  const hasMission = !!unlockedTasks[MISSION_TASK_KEY];
  const hasHiddenMission = !!scheduleState.hiddenMissionActive;
  const hiddenMissionStatus = scheduleState.hiddenMissionStatus ?? 'idle';
  const hiddenMissionStartedAt = scheduleState.hiddenMissionStartedAt ?? null;
  const hiddenTimer = useHiddenMissionTimer(hiddenMissionStatus, hiddenMissionStartedAt);
  const { play } = useSoundEffect();

  const MISSION_TASK = {
    badge: '隱藏任務', badgeColor: 'bg-[#c06b9a]/20 text-[#c06b9a]',
    name: '✦ 小天使揭曉儀式',
    desc: '由小主人公開指認自己的小天使，並親手將禮物交給小主人。\n\n小主人猜對小天使 → 可為隊伍加分\n小主人滿意禮物 → 可為小天使隊伍加分',
  };

  const getEventTasks = (dayId: string, eventIdx: number, baseTasks: typeof scheduleDays[0]['events'][0]['tasks']) => {
    // day1 idx 4 = 閨蜜夜話 (hidden mission is NOT in events array, so index is unchanged)
    if (dayId === 'day1' && eventIdx === 4 && hasMission) return [...baseTasks, MISSION_TASK];
    return baseTasks;
  };

  // The hidden mission is rendered as a virtual event inserted after HIDDEN_MISSION_INSERT_AFTER_IDX
  // It uses a special key 'hidden' to distinguish from real events
  const HIDDEN_EVENT_VIRTUAL_KEY = 'hidden-mission-virtual';

  const [activeDay, setActiveDay]       = useState('day0');
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});
  const [claimedMap, setClaimedMap]     = useState<Record<string, boolean>>(getClaimedMap);
  const [rewardPopup, setRewardPopup]   = useState<RewardPopup | null>(null);
  const [claiming, setClaiming]         = useState<string | null>(null);
  const [showDayComplete, setShowDayComplete] = useState(false);

  // ── 啟動時用 DB history 驗證 localStorage 已領取記錄 ──────────────────────
  // 如果 localStorage 說已領取但 DB history 裡沒有這個人的任務獎勵記錄，
  // 代表之前 RPC 失敗，清除 localStorage 讓玩家可以重新領取
  useEffect(() => {
    if (!currentMember || scores.history.length === 0) return;
    const localClaimed = getClaimedMap();
    const claimedKeys = Object.keys(localClaimed).filter(k => localClaimed[k]);
    if (claimedKeys.length === 0) return;

    // 找出 DB history 裡這個玩家的所有任務獎勵記錄
    const dbClaimedNotes = new Set(
      scores.history
        .filter(e => e.memberId === currentMember.id && e.note.startsWith('任務完成獎勵：'))
        .map(e => e.note.replace('任務完成獎勵：', '').trim())
    );

    // 找出所有活動標題，建立 eventKey → title 的對應
    const eventKeyToTitle: Record<string, string> = {};
    scheduleDays.forEach(day => {
      day.events.forEach((ev, idx) => {
        eventKeyToTitle[buildEventKey(day.id, idx)] = ev.title;
      });
    });
    // 加入 hidden mission
    eventKeyToTitle['hidden-mission-virtual'] = '微光記憶：民宿密令';
    // 加入 custom tasks
    customTasks.forEach(t => {
      eventKeyToTitle[`custom-${t.id}`] = t.title;
    });

    // 檢查每個 localStorage 已領取的 key，如果 DB 裡沒有對應記錄就清除
    let needUpdate = false;
    const newMap = { ...localClaimed };
    claimedKeys.forEach(key => {
      const title = eventKeyToTitle[key];
      if (title && !dbClaimedNotes.has(title)) {
        // DB 裡沒有這筆記錄，清除 localStorage 讓玩家重新領取
        delete newMap[key];
        needUpdate = true;
      }
    });

    if (needUpdate) {
      localStorage.setItem(CLAIMED_KEY, JSON.stringify(newMap));
      setClaimedMap(newMap);
    }
  // 只在 scores.history 或 currentMember 變化時執行一次
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores.history, currentMember?.id]);

  // ── Listen for navigate-schedule event (from LiveScoreBoard comment click) ──
  useEffect(() => {
    const handler = (e: Event) => {
      const { taskTitle } = (e as CustomEvent<{ taskTitle: string }>).detail;
      if (!taskTitle) return;

      // 1. 先搜尋臨時任務
      const matchedCustom = customTasks.find(t => t.title === taskTitle);
      if (matchedCustom) {
        setActiveDay(matchedCustom.dayId);
        setTimeout(() => {
          const el = document.getElementById('schedule-custom-tasks');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.transition = 'box-shadow 0.3s ease';
            el.style.boxShadow = '0 0 0 2px #e0555570, 0 0 30px rgba(224,85,85,0.25)';
            setTimeout(() => { el.style.boxShadow = ''; }, 1400);
          }
        }, 250);
        return;
      }

      // 2. 搜尋正式行程的活動標題或任務名稱
      let foundDayId: string | null = null;
      let foundEventKey: string | null = null;
      scheduleDays.forEach(day => {
        day.events.forEach((ev, evIdx) => {
          // 比對活動標題
          if (!foundEventKey && (ev.title === taskTitle || ev.title.includes(taskTitle) || taskTitle.includes(ev.title.replace(/[「」【】：:]/g, '')))) {
            foundDayId = day.id;
            foundEventKey = buildEventKey(day.id, evIdx);
          }
          // 比對任務名稱
          ev.tasks.forEach(task => {
            if (!foundEventKey && (task.name === taskTitle || task.name.includes(taskTitle) || taskTitle.includes(task.name))) {
              foundDayId = day.id;
              foundEventKey = buildEventKey(day.id, evIdx);
            }
          });
        });
      });

      if (foundDayId && foundEventKey) {
        setActiveDay(foundDayId);
        setTimeout(() => {
          const el = document.getElementById(`schedule-event-${foundEventKey}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.transition = 'box-shadow 0.4s ease';
            el.style.boxShadow = '0 0 0 2px #d4a84360, 0 0 30px rgba(212,168,67,0.25)';
            setTimeout(() => { el.style.boxShadow = ''; }, 1600);
          }
        }, 320);
      }
    };
    window.addEventListener('srs-navigate-schedule', handler);
    return () => window.removeEventListener('srs-navigate-schedule', handler);
  }, [customTasks]);

  // Unlock animation state
  const [newlyUnlocked, setNewlyUnlocked]   = useState<Set<string>>(new Set());
  const [unlockToasts, setUnlockToasts]     = useState<UnlockToast[]>([]);
  const prevUnlockedRef  = useRef<Record<string, boolean> | null>(null);
  const prevPctRef       = useRef<Record<string, number>>({});

  // ── Reset on game reset ───────────────────────────────────────────────────
  useEffect(() => {
    const handleReset = () => setClaimedMap({});
    window.addEventListener('srs-rewards-reset', handleReset);
    return () => window.removeEventListener('srs-rewards-reset', handleReset);
  }, []);

  // ── Detect day completion ─────────────────────────────────────────────────
  useEffect(() => {
    scheduleDays.forEach(day => {
      const total = day.events.length; if (total === 0) return;
      const done = day.events.filter((_, i) => (eventStatuses[buildEventKey(day.id, i)] as TaskStatus ?? 'pending') === 'done').length;
      const pct = Math.round((done / total) * 100);
      const prev = prevPctRef.current[day.id];
      if (prev !== undefined && prev < 100 && pct === 100 && day.id === activeDay) setShowDayComplete(true);
      prevPctRef.current[day.id] = pct;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventStatuses]);

  // ── Detect newly unlocked tasks ───────────────────────────────────────────
  useEffect(() => {
    // First render: capture current state without triggering animation
    if (prevUnlockedRef.current === null) {
      prevUnlockedRef.current = { ...unlockedTasks };
      return;
    }
    const newKeys = Object.keys(unlockedTasks).filter(
      key => unlockedTasks[key] && !prevUnlockedRef.current![key]
    );
    if (newKeys.length > 0) {
      setNewlyUnlocked(prev => { const n = new Set(prev); newKeys.forEach(k => n.add(k)); return n; });

      // Build toasts with human-readable task names
      const toastItems: UnlockToast[] = newKeys.map(key => {
        // Try to find task name from scheduleData
        let taskName = '新任務';
        scheduleDays.forEach(day => {
          day.events.forEach((ev, evIdx) => {
            ev.tasks.forEach((task, ti) => {
              if (buildTaskKey(day.id, evIdx, ti) === key) taskName = task.name;
            });
            if (key === MISSION_TASK_KEY) taskName = '小天使揭曉儀式';
          });
        });
        return { id: `${key}-${Date.now()}`, taskName };
      });
      setUnlockToasts(prev => [...prev, ...toastItems]);

      // Clean up after animations
      newKeys.forEach(key => {
        setTimeout(() => setNewlyUnlocked(prev => { const n = new Set(prev); n.delete(key); return n; }), 3500);
      });
      toastItems.forEach(t => {
        setTimeout(() => setUnlockToasts(prev => prev.filter(x => x.id !== t.id)), 4200);
      });
    }
    prevUnlockedRef.current = { ...unlockedTasks };
  }, [unlockedTasks]);

  const getStatus = (dayId: string, idx: number): TaskStatus =>
    (eventStatuses[buildEventKey(dayId, idx)] as TaskStatus) ?? 'pending';

  const toggleTask = (key: string, isUnlocked: boolean) => {
    if (!isUnlocked) return;
    setExpandedTasks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const current = scheduleDays.find(d => d.id === activeDay)!;
  const currentCustomTasks = customTasks.filter(t => t.dayId === activeDay);

  // 用 DB scores.history 雙重驗證是否已領取（防止 localStorage 標記但 DB 沒寫入的情況）
  const isClaimedInDB = useCallback((eventKey: string, memberId: string): boolean => {
    const note = `任務完成獎勵：`;
    // 找 history 裡有沒有這個 eventKey + memberId 的記錄
    return scores.history.some(e =>
      e.memberId === memberId &&
      e.note.includes(note) &&
      e.note.includes(eventKey.replace(/^day\d+-/, '').replace(/-\d+$/, '') || eventKey)
    );
  }, [scores.history]);

  const handleClaimReward = useCallback(async (eventKey: string, eventTitle: string) => {
    if (!selectedTeam || !currentMember) return;
    // localStorage 快取檢查（快速路徑）
    if (claimedMap[eventKey]) return;
    // 防止重複點擊
    if (claiming) return;

    setClaiming(eventKey);
    play('reward');

    // 短暫延遲讓動畫播放
    await new Promise(r => setTimeout(r, 600));

    const points = Math.floor(Math.random() * 10) + 1;

    try {
      // addScore 現在是 async（內部用 RPC），等它真正完成
      await addScore(selectedTeam, 'chemistry', points, `任務完成獎勵：${eventTitle}`, currentMember.id, currentMember.name);

      // RPC 成功後才標記 localStorage
      markClaimed(eventKey);
      saveMemberRewardEntry(currentMember.id, {
        eventKey, eventTitle, dayLabel: current.label,
        points, timestamp: Date.now(), team: selectedTeam,
      });
      setClaimedMap(getClaimedMap());
      setRewardPopup({ points, team: selectedTeam, eventTitle });
      play('success');
    } catch (err) {
      console.error('[handleClaimReward] failed:', err);
      // 失敗時不標記已領取，讓玩家可以重試
    } finally {
      setClaiming(null);
    }
  }, [selectedTeam, currentMember, claimedMap, claiming, addScore, current, play, isClaimedInDB]);

  const getDayProgress = (day: typeof scheduleDays[0]) => {
    const total = day.events.length; if (total === 0) return { total: 0, done: 0, ongoing: 0 };
    const done    = day.events.filter((_, i) => getStatus(day.id, i) === 'done').length;
    const ongoing = day.events.filter((_, i) => getStatus(day.id, i) === 'ongoing').length;
    return { total, done, ongoing };
  };

  const teamLabel = selectedTeam === 'black' ? '黑騎士隊' : '白騎士隊';
  const teamColor = selectedTeam === 'black' ? '#d4a843' : '#c0c0c0';

  const getTaskDisplay = (dayId: string, eventIdx: number, taskIdx: number, task: typeof scheduleDays[0]['events'][0]['tasks'][0]) => {
    const key = buildTaskKey(dayId, eventIdx, taskIdx);
    const ov = taskOverrides[key];
    return {
      name: ov?.name ?? task.name,
      desc: ov?.desc ?? task.desc,
      badge: task.badge,
      badgeColor: task.badgeColor,
      isOverridden: !!ov,
    };
  };

  const getEventDisplay = (dayId: string, eventIdx: number, ev: typeof scheduleDays[0]['events'][0]) => {
    const key = buildEventKey(dayId, eventIdx);
    const ov = eventOverrides[key];
    return {
      title: ov?.title ?? ev.title,
      time:  ov?.time  ?? ev.time,
      isOverridden: !!ov,
    };
  };

  return (
    <section id="schedule" className="relative overflow-hidden py-14 sm:py-20 px-5" style={{ background: '#020b1a' }}>

      {/* ── Overlays ── */}
      {showDayComplete && <DayCompleteEffect onDone={() => setShowDayComplete(false)} />}
      <UnlockToastStack toasts={unlockToasts} />

      {/* ── BG decoration ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(77,168,255,0.05) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(77,168,255,0.3), transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(77,168,255,0.15), transparent)' }} />
        <div className="absolute top-5 left-5 w-7 h-7 border-l border-t border-[#4da8ff]/20" />
        <div className="absolute top-5 right-5 w-7 h-7 border-r border-t border-[#4da8ff]/18" />
        <div className="absolute bottom-5 left-5 w-7 h-7 border-l border-b border-[#4da8ff]/12" />
        <div className="absolute bottom-5 right-5 w-7 h-7 border-r border-b border-[#4da8ff]/12" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-56 opacity-25 rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(30,90,220,0.6) 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-5xl mx-auto">

        {/* ── Reward Popup ── */}
        {rewardPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setRewardPopup(null)}>
            <div
              className="relative border rounded-2xl px-10 py-10 text-center max-w-sm w-full mx-4 animate-[rewardPop_0.4s_cubic-bezier(0.175,0.885,0.32,1.275)]"
              style={{ background: '#04101e', borderColor: rewardPopup.team === 'black' ? '#d4a84360' : '#c0c0c060' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute inset-0 rounded-2xl opacity-15 pointer-events-none" style={{ boxShadow: `0 0 60px 20px ${rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0'}` }} />
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: `${rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0'}18`, border: `2px solid ${rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0'}50` }}>
                <i className="ri-gift-2-line text-4xl" style={{ color: rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0' }} />
              </div>
              <p className="text-[#4a6a8a] text-xs tracking-widest uppercase mb-2">任務完成獎勵</p>
              <p className="text-white text-sm font-bold mb-5 leading-snug">{rewardPopup.eventTitle}</p>
              <div className="mb-2">
                <span className="font-black" style={{ fontSize: '4rem', lineHeight: 1, color: rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0' }}>+{rewardPopup.points}</span>
              </div>
              <p className="text-[#4a6a8a] text-xs mb-1">默契分數</p>
              <p className="text-sm font-bold mb-7" style={{ color: rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0' }}>{teamLabel} 已獲得</p>
              <button onClick={() => { setRewardPopup(null); play('click'); }} className="px-8 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap text-black" style={{ background: rewardPopup.team === 'black' ? '#d4a843' : '#c0c0c0' }}>太棒了！</button>
            </div>
          </div>
        )}

        {/* ── Section title ── */}
        <div className="text-center mb-9 sm:mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-16 sm:w-28" style={{ background: 'linear-gradient(90deg, transparent, #4da8ff40)' }} />
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ background: '#4da8ff12', border: '1px solid #4da8ff35' }}>
              <i className="ri-map-2-line text-[#4da8ff] text-xs" />
              <span className="text-[#4da8ff] text-[10px] tracking-[0.4em] uppercase font-black font-cinzel">Adventure Journey</span>
            </div>
            <div className="h-px w-16 sm:w-28" style={{ background: 'linear-gradient(90deg, #4da8ff40, transparent)' }} />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mt-2 mb-9 font-serif" style={{ textShadow: '0 0 40px rgba(77,168,255,0.25)' }}>冒險旅程</h2>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10" style={{ background: '#4da8ff40' }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4da8ff', boxShadow: '0 0 8px #4da8ff' }} />
            <div className="h-px w-10" style={{ background: '#4da8ff40' }} />
          </div>
        </div>

        {/* ── Day tabs ── */}
        <div className="mb-5 sm:mb-8">
          <div className="flex items-center justify-center gap-1.5 rounded-xl p-1 w-full" style={{ background: '#04112a', border: '1px solid #0d2040' }}>
            {scheduleDays.map(d => {
              const { total, done, ongoing } = getDayProgress(d);
              const isActive = activeDay === d.id;
              return (
                <button key={d.id} onClick={() => { setActiveDay(d.id); play('click'); }}
                  className="py-2.5 sm:py-3 px-3 sm:px-5 rounded-lg text-xs sm:text-sm font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap relative flex-1 sm:flex-none"
                  style={{ background: isActive ? `${d.color}20` : 'transparent', border: isActive ? `1px solid ${d.color}45` : '1px solid transparent', color: isActive ? d.color : '#2a4a68', boxShadow: isActive ? `0 0 14px ${d.color}20` : 'none' }}
                >
                  <span>{d.label}</span>
                  {total > 0 && (
                    <div className="flex justify-center gap-0.5 mt-1.5">
                      {Array.from({ length: total }).map((_, i) => {
                        const s = getStatus(d.id, i);
                        return <div key={i} className="w-1.5 h-1.5 rounded-full transition-colors" style={{ background: s === 'done' ? '#7ec8a0' : s === 'ongoing' ? '#4da8ff' : '#0d2040' }} />;
                      })}
                    </div>
                  )}
                  {ongoing > 0 && (
                    <span className="absolute -top-1.5 -right-1 w-4 h-4 rounded-full bg-[#4da8ff] text-[#020b1a] text-[9px] font-black flex items-center justify-center" style={{ boxShadow: '0 0 8px #4da8ff80' }}>
                      {ongoing}
                    </span>
                  )}
                  {total > 0 && done === total && (
                    <span className="absolute -top-1.5 -right-1 w-4 h-4 rounded-full bg-[#7ec8a0] flex items-center justify-center">
                      <i className="ri-check-line text-[9px] text-[#020b1a] font-black" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Day header ── */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-4 mb-4 p-3 rounded-2xl border relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${current.color}12, ${current.color}04)`, borderColor: `${current.color}28` }}>
            <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
              <div className="h-full" style={{ background: `linear-gradient(90deg, transparent, ${current.color}60, transparent)`, animation: 'liveScan 3s linear infinite' }} />
            </div>
            <div className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0 relative z-10"
              style={{ background: `linear-gradient(135deg, ${current.color}28, ${current.color}0a)`, border: `1.5px solid ${current.color}45`, boxShadow: `0 0 20px ${current.color}30` }}>
              <i className={`${current.icon} text-xl`} style={{ color: current.color }} />
            </div>
            <div className="flex-1 min-w-0 relative z-10">
              <h3 className="text-white text-lg font-black mb-1 flex items-center gap-2 flex-wrap">
                {(() => {
                  const match = current.title.match(/^(【.+?】)(.+)$/);
                  if (match) return (
                    <>
                      <span className="text-xs font-black tracking-widest px-2 py-0.5 rounded shrink-0" style={{ background: `${current.color}18`, color: current.color, border: `1px solid ${current.color}30` }}>
                        {match[1].replace(/【|】/g, '')}
                      </span>
                      <span className="leading-tight">{match[2]}</span>
                    </>
                  );
                  return current.title;
                })()}
              </h3>
              <p className="text-xs font-bold tracking-wider" style={{ color: `${current.color}90` }}>{current.theme}</p>
            </div>
          </div>

          {current.events.length > 0 && (() => {
            const { total, done, ongoing } = getDayProgress(current);
            const pct = Math.round((done / total) * 100);
            return (
              <div className="rounded-xl border px-4 py-3 flex items-center gap-4" style={{ background: '#03101f', borderColor: '#0d2040' }}>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#2a4a68] text-xs tracking-widest uppercase">今日進度</span>
                    <span className="text-[#60a8d8] text-xs font-black">{done} <span className="text-[#1a3a58]">/</span> {total}</span>
                  </div>
                  <div className="h-2 bg-[#0a1e35] rounded-full overflow-hidden relative">
                    <div className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
                      style={{ width: `${pct}%`, background: pct === 100 ? 'linear-gradient(90deg, #7ec8a0, #5aaa82)' : 'linear-gradient(90deg, #4da8ff, #60c8ff)' }}>
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'liveScan 2s linear infinite' }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs shrink-0">
                  {ongoing > 0 && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold" style={{ background: '#4da8ff18', color: '#4da8ff', border: '1px solid #4da8ff35' }}>
                      <i className="ri-loader-4-line animate-spin text-xs" /> {ongoing}
                    </span>
                  )}
                  {pct === 100 && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold" style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a030' }}>
                      <i className="ri-checkbox-circle-fill text-xs" /> 全數完成
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Timeline ── */}
        {(current.events.length > 0 || currentCustomTasks.length > 0) ? (
          <div className="relative pl-6 border-l border-[#0d2040]">

            {/* ── Custom Tasks (置頂) ── */}
            {currentCustomTasks.length > 0 && (() => {
              const ongoingCount = currentCustomTasks.filter(t => (t.status ?? 'ongoing') === 'ongoing').length;
              const doneCount = currentCustomTasks.filter(t => t.status === 'done').length;
              const allDone = doneCount === currentCustomTasks.length;

              return (
                <div id="schedule-custom-tasks" className="mb-4 relative">
                  {/* timeline dot */}
                  <div className="absolute -left-[1.85rem] top-5 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                    style={{ background: '#e0555518', borderColor: '#e0555560' }}>
                    {allDone
                      ? <i className="ri-check-line text-[8px] text-[#7ec8a0]" />
                      : <div className="w-1.5 h-1.5 rounded-full bg-[#e05555] animate-pulse" style={{ boxShadow: '0 0 6px #e05555' }} />
                    }
                  </div>

                  <div className="border rounded-xl overflow-hidden ring-1 ring-[#e05555]/10"
                    style={{
                      background: allDone ? 'linear-gradient(135deg, #060d06, #040908)' : 'linear-gradient(135deg, #150404, #0a0505)',
                      borderColor: allDone ? '#7ec8a025' : '#e0555530',
                      boxShadow: allDone ? 'none' : '0 0 20px rgba(224,85,85,0.10)',
                    }}
                  >
                    {/* header */}
                    <div className="px-4 pt-3 pb-3 border-b relative overflow-hidden"
                      style={{ borderColor: allDone ? '#7ec8a015' : '#e0555520' }}>
                      {!allDone && (
                        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                          <div className="h-full" style={{ background: 'linear-gradient(90deg, transparent, #e0555570, transparent)', animation: 'liveScan 3s linear infinite' }} />
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap"
                          style={{ background: '#e0555515', color: '#e05555', border: '1px solid #e0555530' }}>
                          🚨 裁判置頂
                        </span>
                        {allDone ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black border whitespace-nowrap"
                            style={{ borderColor: '#7ec8a030', color: '#7ec8a0', background: '#7ec8a015' }}>
                            <i className="ri-checkbox-circle-fill text-xs" />
                            全數完成
                          </div>
                        ) : ongoingCount > 0 ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black border whitespace-nowrap"
                            style={{ borderColor: '#e0555550', color: '#e05555', background: '#e0555518' }}>
                            <i className="ri-loader-4-line text-xs animate-spin" />
                            {doneCount}/{currentCustomTasks.length} 完成
                          </div>
                        ) : null}
                      </div>
                      <h4 className="text-white font-black text-sm flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                          style={{
                            background: allDone ? '#7ec8a015' : '#e0555518',
                            border: `1px solid ${allDone ? '#7ec8a025' : '#e0555530'}`,
                          }}>
                          <i className={allDone ? 'ri-check-double-line text-sm' : 'ri-alarm-warning-line text-sm'}
                            style={{ color: allDone ? '#7ec8a0' : '#e05555' }} />
                        </div>
                        <span>裁判臨時任務</span>
                      </h4>
                    </div>

                    {/* custom task rows */}
                    <div className="px-4 py-3 flex flex-col gap-1">
                      {currentCustomTasks.map(task => (
                        <CustomTaskRow
                          key={task.id}
                          task={task}
                          isClaimed={!!claimedMap[`custom-${task.id}`]}
                          isClaiming={claiming === `custom-${task.id}`}
                          selectedTeam={selectedTeam}
                          currentMember={currentMember}
                          onClaim={handleClaimReward}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {(() => {
              // Build a display list: real events + virtual hidden mission inserted at the right position
              type DisplayItem =
                | { kind: 'real'; ev: typeof current.events[0]; idx: number }
                | { kind: 'hidden' };

              const displayItems: DisplayItem[] = [];
              current.events.forEach((ev, idx) => {
                displayItems.push({ kind: 'real', ev, idx });
                // Insert hidden mission after HIDDEN_MISSION_INSERT_AFTER_IDX in the correct day
                if (
                  hasHiddenMission &&
                  current.id === HIDDEN_MISSION_INSERT_AFTER_DAY &&
                  idx === HIDDEN_MISSION_INSERT_AFTER_IDX
                ) {
                  displayItems.push({ kind: 'hidden' });
                }
              });

              return displayItems.map((item, displayIdx) => {
                if (item.kind === 'hidden') {
                  // ── Render hidden mission as standalone card ──
                  const ev = HIDDEN_MISSION_EVENT;
                  // hiddenMissionActive 為 true 時，最低狀態為 ongoing（不顯示「待解鎖」）
                  const status: TaskStatus =
                    hiddenMissionStatus === 'done'    ? 'done'    : 'ongoing';
                  const cfg    = STATUS_CONFIG[status];
                  const sblue  = STATUS_BLUE[status];
                  const isDone = status === 'done';
                  const isOngoing = status === 'ongoing';
                  const isClaimed = !!claimedMap[HIDDEN_EVENT_VIRTUAL_KEY];
                  const isCurrentlyClaiming = claiming === HIDDEN_EVENT_VIRTUAL_KEY;

                  return (
                    <div key={HIDDEN_EVENT_VIRTUAL_KEY} id={`schedule-event-${HIDDEN_EVENT_VIRTUAL_KEY}`} className="mb-4 relative">
                      {/* timeline dot */}
                      <div className="absolute -left-[1.85rem] top-5 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-300"
                        style={{ backgroundColor: sblue.bg, borderColor: '#c06b9a' }}>
                        {isDone ? <i className="ri-check-line text-[8px] text-[#7ec8a0]" />
                          : isOngoing ? <div className="w-1.5 h-1.5 rounded-full bg-[#c06b9a] animate-pulse" style={{ boxShadow: '0 0 6px #c06b9a' }} />
                          : <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#c06b9a' }} />}
                      </div>

                      <div
                        className={`border rounded-xl overflow-hidden transition-all duration-300 ${isOngoing ? 'ring-1 ring-[#c06b9a]/20' : ''}`}
                        style={{
                          background: isOngoing ? 'linear-gradient(135deg, #1a0418, #0d0210)' : isDone ? '#0d0210' : '#120415',
                          borderColor: isDone ? '#7ec8a030' : isOngoing ? '#c06b9a35' : '#c06b9a20',
                          boxShadow: isOngoing ? '0 0 24px rgba(192,107,154,0.12)' : 'none',
                        }}
                      >
                        {/* event header */}
                        <div className="px-4 pt-3 pb-3 border-b relative overflow-hidden"
                          style={{ borderColor: isDone ? '#7ec8a015' : '#c06b9a20' }}>
                          {isOngoing && (
                            <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                              <div className="h-full" style={{ background: 'linear-gradient(90deg, transparent, #c06b9a70, transparent)', animation: 'liveScan 2.5s linear infinite' }} />
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap"
                              style={{ background: '#c06b9a15', color: '#c06b9a', border: '1px solid #c06b9a30' }}>
                              {ev.time}
                            </span>
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black border whitespace-nowrap"
                              style={{ borderColor: `${sblue.color}50`, color: sblue.color, background: sblue.bg }}>
                              <i className={`${cfg.icon} text-xs ${isOngoing ? 'animate-spin' : ''}`} />
                              {cfg.label}
                            </div>
                          </div>
                          <h4 className="text-white font-black text-sm flex items-center gap-2.5">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                              style={{ background: '#c06b9a18', border: '1px solid #c06b9a30' }}>
                              <i className={`${ev.icon} text-sm`} style={{ color: '#c06b9a' }} />
                            </div>
                            <span className="leading-snug" style={{ color: '#e090c0' }}>{ev.title}</span>
                          </h4>
                        </div>

                        {/* countdown (ongoing only) */}
                        {isOngoing && (
                          <div className="px-4 pt-3 pb-0">
                            <div className="rounded-xl px-3 py-2.5 flex items-center gap-3"
                              style={{ background: hiddenTimer.isExpired ? 'rgba(224,85,85,0.08)' : 'rgba(212,168,67,0.08)', border: `1px solid ${hiddenTimer.isExpired ? 'rgba(224,85,85,0.25)' : 'rgba(212,168,67,0.2)'}` }}>
                              <i className={`ri-timer-line text-base shrink-0 ${hiddenTimer.isExpired ? 'text-[#e05555]' : 'text-[#d4a843]'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-[9px] font-bold mb-0.5" style={{ color: hiddenTimer.isExpired ? 'rgba(224,85,85,0.6)' : 'rgba(212,168,67,0.55)' }}>
                                  {hiddenTimer.isExpired ? '時間已到！' : '剩餘時間'}
                                </p>
                                <span className="text-xl font-black tabular-nums"
                                  style={{ color: hiddenTimer.isExpired ? '#e05555' : hiddenTimer.remainingSeconds < 300 ? '#e05555' : '#d4a843', fontFamily: "'Orbitron', monospace" }}>
                                  {hiddenTimer.display.formatted}
                                </span>
                                <div className="h-1 rounded-full mt-1 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                  <div className="h-full rounded-full transition-all duration-1000"
                                    style={{ width: `${hiddenTimer.progressPct}%`, background: hiddenTimer.isExpired ? '#e05555' : hiddenTimer.remainingSeconds < 300 ? 'linear-gradient(90deg,#e05555,#d4a843)' : 'linear-gradient(90deg,#d4a843,#f0c060)' }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* task content */}
                        <div className={`px-4 py-3 transition-all duration-300 ${isDone ? 'opacity-70' : ''}`}>
                          {ev.tasks.length > 0 && (
                            <div className="rounded-lg border" style={{ background: '#c06b9a08', borderColor: '#c06b9a25' }}>
                              <div className="px-3 py-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap bg-[#c06b9a]/20 text-[#c06b9a]">
                                    {ev.tasks[0].badge}
                                  </span>
                                  <span className="text-[#c06b9a] text-xs font-bold">{ev.tasks[0].name}</span>
                                </div>
                                <p className="text-[#8a5a7a] text-xs leading-relaxed whitespace-pre-line">{ev.tasks[0].desc}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* reward button when done */}
                        {isDone && (
                          <div className="px-4 pb-4 border-t pt-3" style={{ borderColor: '#c06b9a15' }}>
                            {!selectedTeam || !currentMember ? (
                              <div className="flex items-center gap-2 text-[#1a3a58] text-xs">
                                <i className="ri-gift-line text-base" /><span>加入隊伍後可領取任務完成獎勵</span>
                              </div>
                            ) : isClaimed ? (
                              <div className="flex items-center gap-2 text-[#7ec8a0] text-xs">
                                <i className="ri-checkbox-circle-fill text-base" /><span className="font-bold">獎勵已領取</span>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleClaimReward(HIDDEN_EVENT_VIRTUAL_KEY, ev.title)}
                                disabled={isCurrentlyClaiming}
                                className="relative w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition-all overflow-hidden group whitespace-nowrap"
                                style={{ background: 'linear-gradient(135deg, #c06b9a18, #c06b9a08)', border: '1px solid #c06b9a40', color: '#c06b9a' }}
                              >
                                {isCurrentlyClaiming
                                  ? <><i className="ri-loader-4-line text-base animate-spin" /><span>開獎中...</span></>
                                  : <>
                                      <i className="ri-gift-2-line text-base" />
                                      <span>領取隱藏任務完成獎勵</span>
                                      <span className="text-[10px] px-2 py-0.5 rounded-full font-black ml-1"
                                        style={{ background: '#c06b9a25', color: '#c06b9a' }}>隨機 1–10 默契分</span>
                                    </>
                                }
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                // ── Render real event ──
                const { ev, idx } = item;
                void displayIdx;
                const rawStatus = getStatus(current.id, idx);
                const status: TaskStatus = rawStatus;
                const cfg     = STATUS_CONFIG[status];
                const sblue   = STATUS_BLUE[status];
                const isDone  = status === 'done';
                const isOngoing = status === 'ongoing';
                const eventKey  = buildEventKey(current.id, idx);
                const isClaimed = !!claimedMap[eventKey];
                const isCurrentlyClaiming = claiming === eventKey;
                const tasks = getEventTasks(current.id, idx, ev.tasks);
                const evDisplay = getEventDisplay(current.id, idx, ev);
                const addedTasksForEvent = taskAdditions[eventKey] ?? [];

              return (
                <div key={idx} id={`schedule-event-${eventKey}`} className="mb-4 relative">
                  {/* timeline dot */}
                  <div className="absolute -left-[1.85rem] top-5 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-300"
                    style={{ backgroundColor: sblue.bg, borderColor: sblue.color }}>
                    {isDone ? <i className="ri-check-line text-[8px] text-[#7ec8a0]" />
                      : isOngoing ? <div className="w-1.5 h-1.5 rounded-full bg-[#4da8ff] animate-pulse" style={{ boxShadow: '0 0 6px #4da8ff' }} />
                      : <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.color }} />}
                  </div>

                  <div
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${isDone ? 'opacity-75' : isOngoing ? 'ring-1 ring-[#4da8ff]/20' : ''}`}
                    style={{
                      background: isOngoing ? 'linear-gradient(135deg, #041830, #020c1e)' : isDone ? '#03101f' : '#04131f',
                      borderColor: sblue.border,
                      boxShadow: isOngoing ? '0 0 24px rgba(77,168,255,0.12), inset 0 0 20px rgba(77,168,255,0.04)' : 'none',
                    }}
                  >
                    {/* event header */}
                    <div className="px-4 pt-3 pb-3 border-b relative overflow-hidden" style={{ borderColor: sblue.border }}>
                      {isOngoing && (
                        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                          <div className="h-full" style={{ background: 'linear-gradient(90deg, transparent, #4da8ff70, transparent)', animation: 'liveScan 2.5s linear infinite' }} />
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: '#4da8ff15', color: '#4da8ff', border: '1px solid #4da8ff30' }}>{evDisplay.time}</span>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black border whitespace-nowrap" style={{ borderColor: `${sblue.color}50`, color: sblue.color, background: sblue.bg }}>
                          <i className={`${cfg.icon} text-xs ${isOngoing ? 'animate-spin' : ''}`} />
                          {cfg.label}
                        </div>
                      </div>
                      <h4 className="text-white font-black text-sm flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ background: '#c06b9a18', border: '1px solid #c06b9a30' }}>
                          <i className={`${ev.icon} text-sm`} style={{ color: '#c06b9a' }} />
                        </div>
                        <span className="leading-snug">{evDisplay.title}</span>
                      </h4>
                    </div>

                    {/* tasks */}
                    <div className={`px-4 py-3 flex flex-col gap-1 transition-all duration-300 ${isDone ? 'opacity-70' : ''}`}>
                      {(
                        <>
                          {tasks.map((task, ti) => {
                            const isMissionTask = task.name.startsWith('✦');
                            const tKey      = isMissionTask ? MISSION_TASK_KEY : buildTaskKey(current.id, idx, ti);

                            // Skip deleted original tasks
                            if (!isMissionTask && taskDeletions[tKey]) return null;

                            const isUnlocked  = !!unlockedTasks[tKey];
                            const isExpanded  = !!expandedTasks[tKey];
                            const isNew       = newlyUnlocked.has(tKey);
                            const display     = isMissionTask ? { name: task.name, desc: task.desc, badge: task.badge, badgeColor: task.badgeColor, isOverridden: false } : getTaskDisplay(current.id, idx, ti, task);

                            return (
                              <div key={ti}>
                                {/* ── Task row ── */}
                                <button
                                  onClick={() => toggleTask(tKey, isUnlocked)}
                                  className={`w-full flex items-center gap-2 py-2 px-2 rounded-md text-left group relative overflow-hidden ${isUnlocked ? 'hover:bg-[#071828] cursor-pointer' : 'cursor-not-allowed'} ${isMissionTask ? 'border border-[#c06b9a]/20 rounded-lg mb-1' : ''}`}
                                  style={isMissionTask ? { background: '#c06b9a08' } : {}}
                                >
                                  {/* ── Newly-unlocked glow bg ── */}
                                  {isNew && (
                                    <span
                                      className="absolute inset-0 rounded-md pointer-events-none"
                                      style={{ animation: 'taskUnlockBg 3s ease-out forwards' }}
                                    />
                                  )}

                                  {/* ── Left icon ── */}
                                  {isMissionTask ? (
                                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                                      <i className="ri-spy-line text-xs" style={{ color: '#c06b9a' }} />
                                    </div>
                                  ) : isUnlocked ? (
                                    <div
                                      className="w-4 h-4 flex items-center justify-center shrink-0"
                                      style={{ animation: isNew ? 'unlockDotPop 0.6s cubic-bezier(0.175,0.885,0.32,1.275)' : 'none' }}
                                    >
                                      <div
                                        className="w-1.5 h-1.5 rounded-full transition-all duration-200"
                                        style={{
                                          backgroundColor: isNew ? '#4da8ff' : isExpanded ? '#4da8ff' : '#1a3a58',
                                          transform: isExpanded ? 'scale(1.4)' : 'scale(1)',
                                          boxShadow: isNew ? '0 0 8px #4da8ff' : 'none',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                                      <i className="ri-lock-line text-[11px] text-[#1a3050]" />
                                    </div>
                                  )}

                                  {/* ── Task name ── */}
                                  <span
                                    className={`text-sm font-medium flex-1 relative z-10 ${
                                      isMissionTask ? 'font-bold' :
                                      !isUnlocked   ? 'text-[#1a3050]' :
                                      isDone        ? 'line-through text-[#1e4060]' :
                                      isExpanded    ? 'text-white' : 'text-[#5a8ab0]'
                                    }`}
                                    style={{
                                      color: isMissionTask ? '#c06b9a' : isNew ? '#cce8ff' : undefined,
                                      animation: isNew ? 'unlockNameReveal 0.5s 0.1s ease-out both' : 'none',
                                    }}
                                  >
                                    {display.name}
                                  </span>

                                  {/* ── NEW badge (auto-fades) ── */}
                                  {isNew && (
                                    <span
                                      className="shrink-0 text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap relative z-10"
                                      style={{
                                        background: 'linear-gradient(90deg, #4da8ff30, #60d4ff30)',
                                        color: '#4da8ff',
                                        border: '1px solid #4da8ff50',
                                        boxShadow: '0 0 10px rgba(77,168,255,0.4)',
                                        animation: 'unlockNewBadge 3s ease-out forwards',
                                      }}
                                    >
                                      NEW
                                    </span>
                                  )}

                                  {/* ── Arrow / lock badge ── */}
                                  {!isNew && (
                                    isMissionTask ? (
                                      <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 shrink-0 text-[#c06b9a] ${isExpanded ? 'rotate-180' : ''}`} />
                                    ) : isUnlocked ? (
                                      <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`} style={{ color: isExpanded ? '#4da8ff' : '#1a3a58' }} />
                                    ) : (
                                      <span className="text-[10px] text-[#1a3050] px-2 py-0.5 rounded-full border border-[#0d2040] whitespace-nowrap">待解鎖</span>
                                    )
                                  )}
                                </button>

                                {/* ── Sweep line under newly unlocked ── */}
                                {isNew && (
                                  <div className="mx-2 h-px overflow-hidden" style={{ animation: 'unlockSweepLine 1.2s 0.2s ease-out forwards' }}>
                                    <div className="h-full" style={{ background: 'linear-gradient(90deg, transparent, #4da8ffaa, transparent)' }} />
                                  </div>
                                )}

                                {/* ── Expanded detail ── */}
                                {isUnlocked && isExpanded && (
                                  <div
                                    className="mx-2 mb-2 px-3 py-3 rounded-md border-l-2"
                                    style={isMissionTask ? { background: '#c06b9a08', borderColor: '#c06b9a50' } : { background: '#4da8ff08', borderColor: '#4da8ff40' }}
                                  >
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap ${display.badgeColor}`}>{display.badge}</span>
                                      <span className="text-white text-xs font-bold">{display.name.replace('✦ ', '')}</span>
                                    </div>
                                    <p className="text-[#3a6a8a] text-xs leading-relaxed whitespace-pre-line">{display.desc}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* ── Added tasks (shown once per event, outside tasks loop) ── */}
                          {(isOngoing || isDone) && addedTasksForEvent.map((addedTask) => {
                            const addedKey = `added-expand-${addedTask.id}`;
                            const isAddedExpanded = !!expandedTasks[addedKey];
                            const badgeColor = addedTask.badgeColor ?? 'bg-[#d4a843]/20 text-[#d4a843]';

                            return (
                              <div key={addedTask.id}>
                                <button
                                  onClick={() => setExpandedTasks(prev => ({ ...prev, [addedKey]: !prev[addedKey] }))}
                                  className={`w-full flex items-center gap-2 py-2 px-2 rounded-md text-left relative overflow-hidden ${addedTask.desc ? 'hover:bg-[#071828] cursor-pointer' : 'cursor-default'}`}
                                >
                                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isDone ? '#1e4060' : '#4da8ff60' }} />
                                  </div>

                                  <span className="text-sm font-medium flex-1 text-[#5a8ab0] relative z-10">
                                    {addedTask.name}
                                  </span>

                                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold shrink-0 whitespace-nowrap ${badgeColor}`}>
                                    {addedTask.badge}
                                  </span>
                                  <span className="text-[8px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap"
                                    style={{ background: '#4da8ff10', color: '#4da8ff50', border: '1px solid #4da8ff15' }}>
                                    新增
                                  </span>

                                  {addedTask.desc && (
                                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 shrink-0 text-[#1a3a58] ${isAddedExpanded ? 'rotate-180' : ''}`} />
                                  )}
                                </button>

                                {isAddedExpanded && addedTask.desc && (
                                  <div className="mx-2 mb-2 px-3 py-3 rounded-md border-l-2"
                                    style={{ background: '#4da8ff08', borderColor: '#4da8ff40' }}>
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap ${badgeColor}`}>{addedTask.badge}</span>
                                      <span className="text-white text-xs font-bold">{addedTask.name}</span>
                                    </div>
                                    <p className="text-[#3a6a8a] text-xs leading-relaxed whitespace-pre-line">{addedTask.desc}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>


                    {/* ── Reward Button ── */}
                    {isDone && (
                      <div className="px-4 pb-4 border-t border-[#0d2040] pt-3">
                        {!selectedTeam || !currentMember ? (
                          <div className="flex items-center gap-2 text-[#1a3a58] text-xs">
                            <i className="ri-gift-line text-base" /><span>加入隊伍後可領取任務完成獎勵</span>
                          </div>
                        ) : isClaimed ? (
                          <div className="flex items-center gap-2 text-[#7ec8a0] text-xs">
                            <i className="ri-checkbox-circle-fill text-base" /><span className="font-bold">獎勵已領取</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleClaimReward(eventKey, evDisplay.title)}
                            disabled={isCurrentlyClaiming}
                            className="relative w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition-all overflow-hidden group whitespace-nowrap"
                            style={{ background: selectedTeam === 'black' ? 'linear-gradient(135deg, #d4a84318, #d4a84308)' : 'linear-gradient(135deg, #c0c0c018, #c0c0c008)', border: `1px solid ${teamColor}40`, color: teamColor }}
                          >
                            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{ background: `linear-gradient(135deg, ${teamColor}22, ${teamColor}0a)` }} />
                            {isCurrentlyClaiming
                              ? <><i className="ri-loader-4-line text-base animate-spin" /><span>開獎中...</span></>
                              : <>
                                  <i className="ri-gift-2-line text-base" />
                                  <span>領取任務完成獎勵</span>
                                  <span className="text-[10px] px-2 py-0.5 rounded-full font-black ml-1" style={{ background: `${teamColor}25`, color: teamColor }}>隨機 1–10 默契分</span>
                                </>
                            }
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
              }); // end displayItems.map
            })(/* end IIFE */)}

          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: `${current.color}15`, border: `1px solid ${current.color}30` }}>
              <i className={`${current.icon} text-2xl`} style={{ color: current.color }} />
            </div>
            <p className="text-[#2a4a68] text-sm tracking-widest">任務詳情即將揭曉</p>
            <p className="text-[#0d2040] text-xs mt-2">敬請期待</p>
          </div>
        )}

        {/* ── Legend ── */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-6 border-t border-[#0d2040]">
          <p className="text-[#1a3a58] text-xs w-full text-center mb-1">任務狀態由系統管理控制 · 鎖定任務需管理員解鎖後才可查看詳情</p>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const sblue = STATUS_BLUE[key as TaskStatus];
            return (
              <div key={key} className="flex items-center gap-1.5 text-xs" style={{ color: sblue.color }}>
                <i className={cfg.icon} /><span>{cfg.label}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5 text-xs text-[#1a3050]">
            <i className="ri-lock-line" /><span>待解鎖</span>
          </div>
        </div>

        {/* 揭曉儀式 Modal 由 home/page.tsx 全域控制，不在此渲染 */}
      </div>

      <style>{`
        /* ── existing ── */
        @keyframes rewardPop   { from{opacity:0;transform:scale(0.75)} to{opacity:1;transform:scale(1)} }
        @keyframes liveScan    { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

        /* ── unlock toast ── */
        @keyframes unlockToast {
          0%   { opacity:0; transform:translateY(-14px) scale(0.92); }
          12%  { opacity:1; transform:translateY(0) scale(1); }
          75%  { opacity:1; transform:translateY(0) scale(1); }
          100% { opacity:0; transform:translateY(-8px) scale(0.95); }
        }
        @keyframes unlockIconPop {
          0%   { transform:scale(0.5) rotate(-30deg); opacity:0; }
          60%  { transform:scale(1.25) rotate(8deg); opacity:1; }
          100% { transform:scale(1) rotate(0deg); opacity:1; }
        }
        @keyframes unlockRingPulse {
          0%   { transform:scale(1);   opacity:0.7; }
          100% { transform:scale(2.2); opacity:0; }
        }
        @keyframes sparkle {
          from { opacity:0.5; transform:scale(0.9) rotate(-10deg); }
          to   { opacity:1;   transform:scale(1.1) rotate(10deg); }
        }

        /* ── task row unlock ── */
        @keyframes taskUnlockBg {
          0%   { background:rgba(77,168,255,0);    box-shadow:none; }
          20%  { background:rgba(77,168,255,0.14); box-shadow:0 0 20px rgba(77,168,255,0.25) inset; }
          60%  { background:rgba(77,168,255,0.07); box-shadow:0 0 10px rgba(77,168,255,0.12) inset; }
          100% { background:rgba(77,168,255,0);    box-shadow:none; }
        }
        @keyframes unlockDotPop {
          0%   { transform:scale(0.5); opacity:0.3; }
          60%  { transform:scale(1.8); opacity:1; }
          100% { transform:scale(1);   opacity:1; }
        }
        @keyframes unlockNameReveal {
          from { opacity:0.2; transform:translateX(-6px); }
          to   { opacity:1;   transform:translateX(0); }
        }
        @keyframes unlockNewBadge {
          0%   { opacity:0;   transform:scale(0.6); }
          15%  { opacity:1;   transform:scale(1.1); }
          70%  { opacity:1;   transform:scale(1); }
          100% { opacity:0;   transform:scale(0.9); }
        }
        @keyframes unlockSweepLine {
          from { transform:scaleX(0); transform-origin:left; opacity:1; }
          to   { transform:scaleX(1); transform-origin:left; opacity:0; }
        }
      `}</style>
    </section>
  );
}
